// Imports
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { navigate } from 'vike/client/router';

import type { WithFormErrors } from '@/components/Forms/FormControl';
import FormControl from '@/components/Forms/FormControl';
import FormErrors from '@/components/Forms/FormErrors';
import FormSubmitButton from '@/components/Forms/FormSubmitButton';
import PasswordInput from '@/components/Forms/PasswordInput';
import useFormSubmit from '@/components/Forms/useFormSubmit';
import { NotFoundError } from '@/renderer/utils/errors';
import { useRequest } from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

const EMAIL_REGEX = /\S+@\S+\.\S+/;

// Types
type SetNewPasswordData = { email: string; password: string; code: string };

// API requests
const newPasswordRequest: RequestSchema<API.ResetConfirmState, SetNewPasswordData> = {
  name: 'newPasswordRequest',
  input: '/api/v1/auth/reset-confirm2',
  method: 'POST',
};

const authLoginRequest: RequestSchema<
  { message: string; session: API.Session; user: API.User },
  { username: string; password: string }
> = {
  name: 'authLoginRequest',
  input: '/api/v1/auth/login',
  method: 'POST',
};

export default function SetPassword({ email = '', code = '' }) {
  // Form hooks
  const form = useForm<WithFormErrors<SetNewPasswordData>>();

  // Request hooks
  const request = useRequest(newPasswordRequest, { manual: true });
  const authLogin = useRequest(authLoginRequest, { manual: true, hideAlert: true });
  const [success, setSuccess] = useState(false);

  // Handlers
  const handleFormSubmit = useFormSubmit(
    {
      form: form,
      callback: async (body) => {
        try {
          const result = await request.run(body, { throwOnRun: true });

          if (result?.message === 'API.AUTHENTICATION.RESET.CONFIRMED') {
            setSuccess(true);
            await authLogin.run(
              { username: body.email, password: body.password },
              { throwOnRun: true },
            );
            navigate('/dashboard');
          } else {
            form.setError('code', {
              type: 'server',
              message: 'This code is either invalid or expired. Please request a new one.',
            });
          }
        } catch (error) {
          if (error instanceof NotFoundError) {
            form.setError('code', {
              type: 'server',
              message:
                'Either this code or email are invalid or expired. Please request a new one.',
            });
          } else {
            throw error;
          }
        }
      },
    },
    [],
  );

  return (
    <>
      <h1 className="text-center text-2xl font-bold text-zinc-900 dark:text-zinc-200">
        Set new password
      </h1>
      <p className="text-center text-sm">
        Enter your email and the new password you'd like to use to access your account.
      </p>
      <form onSubmit={handleFormSubmit} method="POST" className="space-y-6">
        <FormControl
          form={form}
          name="email"
          label="Primary email address"
          defaultValue={email}
          register={{
            required: {
              value: true,
              message: 'Email is required',
            },
            pattern: {
              value: EMAIL_REGEX,
              message: 'Email is not a valid email!',
            },
          }}
        />

        <FormControl
          form={form}
          name="password"
          label="New password"
          wrap={PasswordInput}
          register={{
            required: {
              value: true,
              message: 'Password is required',
            },
            minLength: {
              value: 8,
              message: 'Password must be at least 8 characters long',
            },
          }}
        />

        <FormControl form={form} name="code" defaultValue={code} type="hidden" />

        <FormErrors form={form} request={request} />

        <FormSubmitButton
          disabled={request.loading || success}
          loading={request.loading || success}
        >
          {success ? 'Success! Signing in...' : 'Update password'}
        </FormSubmitButton>
        <div className="flex items-center justify-center space-x-2 text-zinc-700 dark:text-zinc-300">
          <span className="text-sm">Wrong place?</span>
          <a href="/signin" className="flex items-center space-x-1 text-sm font-semibold">
            <span>Back to sign in </span>
            <ArrowRightIcon height={16} className="h-4 w-4" />
          </a>
        </div>
      </form>
    </>
  );
}
